import React, { useState } from "react";
import type { CommonPageProps, ChemicalVendor, ChemicalType, Chemical } from "../types";
import { VendorModal } from "../components/modal/modalVendorUpdate";
import { ChemicalTypeModal } from "../components/modal/modalChemicalTypeUpdate";
import { ChemicalModal } from "../components/modal/modalChemicalUpdate";
import DeleteModal from "../components/modal/modalDelete";
import CommonPage from "../components/commonPage";
import { useApi } from "../logic/classApi";
import { useChemicalApi } from "../logic/chemicalApi";
import ManagementTable from "../components/managementTable";
import { PAGE, usePermission } from "../logic/permission";
import Loading from "../components/loading";
import useCookie from "../logic/cookie";
import "./css/pageManagement.css";
import { showErrorMessage } from "../components/appPane";
import { MESSAGES } from "../constants/const";

/** 薬剤販売業者テーブル用タイトル */
const VENDOR_TITLE = "薬剤販売業者";

/** 薬剤販売業者テーブル用ヘッダ情報 */
const VENDOR_HEADERS = {
  vendorName: "会社名",
  vendorPost: "郵便番号",
  vendorAddress1: "住所１",
  vendorAddress2: "住所２",
  vendorTel: "電話番号",
  vendorFax: "FAX番号",
  vendorPerson: "担当者名",
};

/** 薬剤カテゴリテーブル用タイトル */
const TYPE_TITLE = "薬剤カテゴリ";

/** 薬剤カテゴリテーブル用ヘッダ情報 */
const TYPE_HEADERS = { chemicalTypeName: "カテゴリ名" };

/** 薬剤テーブル用タイトル */
const CHEMICAL_TITLE = "薬剤";

/** 薬剤テーブル用ヘッダ情報 */
const CHEMICAL_HEADERS = {
  chemicalName: "薬剤表示名",
  chemicalTypeName: "薬剤カテゴリ",
  vendorName: "担当業者名",
};

/**
 * 薬剤管理画面
 * @param props
 * @returns
 */
const Management: React.FC<CommonPageProps> = ({ showHeader, showSidebar, onLogout }) => {
  // 表示権限チェック
  const [permission] = usePermission(PAGE.MANAGEMENT);
  if (!permission.canDisplay) {
    // 表示権限がない場合は、表示可能画面に自動遷移させるため、ローディング表示のみを返す
    return <Loading />;
  }
  // 支社コード
  const [cookieData] = useCookie();
  const branchCode = cookieData.branchCode;
  // 薬剤API呼び出し
  const api = useApi();
  const [chemicalData, chemicalHandle] = useChemicalApi(api, branchCode);
  // 薬剤販売業者編集情報
  const [vendor, setVendor] = useState<ChemicalVendor>();
  const [showEditVendor, setShowEditVendor] = useState(false);
  const [showDeleteVendor, setShowDeleteVendor] = useState(false);
  // 薬剤カテゴリ編集情報
  const [type, setType] = useState<ChemicalType>();
  const [showEditType, setShowEditType] = useState(false);
  const [showDeleteType, setShowDeleteType] = useState(false);
  // 薬剤編集情報
  const [chemical, setChemical] = useState<Chemical>();
  const [showEditChemical, setShowEditChemical] = useState(false);
  const [showDeleteChemical, setShowDeleteChemical] = useState(false);

  // 非同期処理中フラグ
  const [isProc, setProc] = useState(false);
  // 非同期処理実行
  const doProc = (fn: () => Promise<void>) => {
    if (isProc) return;
    setProc(true);
    try {
      fn();
    } finally {
      setProc(false);
    }
  };

  // ポップアップキャンセル時処理
  const handleCancel = () => {
    setVendor(null);
    setShowEditVendor(false);
    setShowDeleteVendor(false);
    setType(null);
    setShowEditType(false);
    setShowDeleteType(false);
    setChemical(null);
    setShowEditChemical(false);
    setShowDeleteChemical(false);
  };

  // 薬剤販売業者新規登録開始
  const startAddVendor = () => {
    if (!permission.canEdit) return;
    setShowEditVendor(true);
  };

  // 薬剤販売業者編集開始
  const startEditVendor = (vendor: ChemicalVendor) => {
    if (!permission.canEdit) return;
    setVendor(vendor);
    setShowEditVendor(true);
  };

  // 薬剤販売業者削除開始
  const startDeleteVendor = (vendor: ChemicalVendor) => {
    if (!permission.canEdit) return;
    setVendor(vendor);
    setShowDeleteVendor(true);
  };

  // 薬剤販売業者編集
  const handleVendorRegister = (vendor: ChemicalVendor) => {
    doProc(async () => {
      if (!permission.canEdit) return;
      await chemicalHandle.saveVendor(vendor);
      setVendor(null);
      setShowEditVendor(false);
    });
  };

  // 薬剤販売業者削除
  const handleVendorDelete = (vendor: ChemicalVendor) => {
    doProc(async () => {
      if (!permission.canEdit) return;
      await chemicalHandle.deleteVendor(vendor);
      setVendor(null);
      setShowDeleteVendor(false);
    });
  };

  // 薬剤カテゴリ新規登録開始
  const startAddType = () => {
    if (!permission.canEdit) return;
    setShowEditType(true);
  };

  // 薬剤カテゴリ編集開始
  const startEditType = (type: ChemicalType) => {
    if (!permission.canEdit) return;
    setType(type);
    setShowEditType(true);
  };

  // 薬剤カテゴリ削除開始
  const startDeleteType = (type: ChemicalType) => {
    if (!permission.canEdit) return;
    setType(type);
    setShowDeleteType(true);
  };

  // 薬剤カテゴリ編集
  const handleTypeRegister = (type: ChemicalType) => {
    doProc(async () => {
      if (!permission.canEdit) return;
      await chemicalHandle.saveType(type);
      setType(null);
      setShowEditType(false);
    });
  };

  // 薬剤カテゴリ削除
  const handleTypeDelete = (type: ChemicalType) => {
    doProc(async () => {
      if (!permission.canEdit) return;
      await chemicalHandle.deleteType(type);
      setType(null);
      setShowDeleteType(false);
    });
  };

  // 薬剤新規登録開始
  const startAddChemical = () => {
    if (!permission.canEdit) return;
    if (!chemicalData.vendors?.length) {
      showErrorMessage(MESSAGES.ERROR_CHEMICAL_VENDOR_NONE);
      return;
    }
    if (!chemicalData.types?.length) {
      showErrorMessage(MESSAGES.ERROR_CHEMICAL_TYPE_NONE);
      return;
    }
    setShowEditChemical(true);
  };

  // 薬剤編集開始
  const startEditChemical = (chemical: Chemical) => {
    if (!permission.canEdit) return;
    if (!chemicalData.vendors?.length) {
      showErrorMessage(MESSAGES.ERROR_CHEMICAL_VENDOR_NONE);
      return;
    }
    if (!chemicalData.types?.length) {
      showErrorMessage(MESSAGES.ERROR_CHEMICAL_TYPE_NONE);
      return;
    }
    setChemical(chemical);
    setShowEditChemical(true);
  };

  // 薬剤削除開始
  const startDeleteChemical = (chemical: Chemical) => {
    if (!permission.canEdit) return;
    setChemical(chemical);
    setShowDeleteChemical(true);
  };

  // 薬剤編集
  const handleChemicalRegister = (chemical: Chemical) => {
    doProc(async () => {
      if (!permission.canEdit) return;
      await chemicalHandle.saveChemical(chemical);
      setChemical(null);
      setShowEditChemical(false);
    });
  };

  // 薬剤削除
  const handleChemicalDelete = (chemical: Chemical) => {
    doProc(async () => {
      if (!permission.canEdit) return;
      chemicalHandle.deleteChemical(chemical);
      setChemical(null);
      setShowDeleteChemical(false);
    });
  };

  return (
    <CommonPage showHeader={showHeader} showSidebar={showSidebar} onLogout={onLogout}>
      <div className="app-main">
        <section className="vendor-section">
          <h3>{VENDOR_TITLE}</h3>
          <ManagementTable
            title={VENDOR_TITLE}
            datas={chemicalData.vendors}
            headers={VENDOR_HEADERS}
            onAdd={startAddVendor}
            onEdit={startEditVendor}
            onDelete={startDeleteVendor}
            canEdit={permission.canEdit}
          />
          {showEditVendor && (
            <VendorModal vendor={vendor} onClose={handleCancel} onRegister={handleVendorRegister} />
          )}
          {showDeleteVendor && (
            <DeleteModal
              title={VENDOR_TITLE}
              name={vendor.vendorName}
              target={vendor}
              onClose={handleCancel}
              onDelete={handleVendorDelete}
            />
          )}
        </section>

        <section className="type-section">
          <h3>{TYPE_TITLE}</h3>
          <ManagementTable
            title={TYPE_TITLE}
            datas={chemicalData.types}
            headers={TYPE_HEADERS}
            onAdd={startAddType}
            onEdit={startEditType}
            onDelete={startDeleteType}
            canEdit={permission.canEdit}
          />
          {showEditType && (
            <ChemicalTypeModal type={type} onClose={handleCancel} onRegister={handleTypeRegister} />
          )}
          {showDeleteType && (
            <DeleteModal
              title={TYPE_TITLE}
              name={type.chemicalTypeName}
              target={type}
              onClose={handleCancel}
              onDelete={handleTypeDelete}
            />
          )}
        </section>

        <section className="chemical-section">
          <h3>{CHEMICAL_TITLE}</h3>
          <div>
            <ManagementTable
              title={CHEMICAL_TITLE}
              datas={chemicalData.chemicals
                ?.sort((a, b) => a.chemicalSort1 - b.chemicalSort1)
                .map(c => {
                  const vendor = chemicalData.getVendor(c.chemicalId);
                  const type = chemicalData.getType(c.chemicalTypeId);
                  return {
                    ...c,
                    vendorName: vendor?.vendorName,
                    chemicalTypeName: type?.chemicalTypeName,
                  };
                })}
              headers={CHEMICAL_HEADERS}
              onAdd={startAddChemical}
              onEdit={startEditChemical}
              onDelete={startDeleteChemical}
              canEdit={permission.canEdit}
            />
          </div>

          {showEditChemical && (
            <ChemicalModal
              chemical={chemical}
              vendors={chemicalData.vendors}
              types={chemicalData.types}
              onClose={handleCancel}
              onRegister={handleChemicalRegister}
            />
          )}

          {showDeleteChemical && (
            <DeleteModal
              title={CHEMICAL_TITLE}
              name={chemical.chemicalName}
              target={chemical}
              onClose={handleCancel}
              onDelete={handleChemicalDelete}
            />
          )}
        </section>
      </div>
    </CommonPage>
  );
};

export default Management;
